import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Target, LayoutGrid, List as ListIcon, ArrowRightCircle, History, AlertTriangle, Clock, StickyNote, ArrowRight, Sparkles, UserPlus, Lock, FileText, Package, UserCog, Trash2, Users2, ChevronDown, ChevronRight, MapPin, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import ConfirmDelete from "@/components/ConfirmDelete";
import RegionSelect from "@/components/RegionSelect";
import RegionFilter from "@/components/RegionFilter";
import { useAuth } from "@/context/AuthContext";
import ClientForm from "@/components/ClientForm";
import { formatDMYTime } from "@/lib/date";
import { PotentialMachinesEditor, PotentialMachinesSummary, cleanPotentialMachines } from "@/components/PotentialMachines";
import { RevBadge } from "@/components/quotation/QuotationBits";
import { can } from "@/lib/perms";
import { IfCan } from "@/components/PermGate";

const STAGES = ["New", "Contacted", "Demo Scheduled", "Demo Completed", "Quotation Sent", "PO Received", "Lost"];
const TERMINAL_STAGES = new Set(["PO Received", "Lost"]);
const STAGE_COLORS = {
  "New": "border-t-slate-400",
  "Contacted": "border-t-indigo-400",
  "Demo Scheduled": "border-t-amber-400",
  "Demo Completed": "border-t-sky-400",
  "Quotation Sent": "border-t-blue-400",
  "PO Received": "border-t-emerald-500",
  "Lost": "border-t-red-400",
};

const EMPTY = { company_name: "", contact_person: "", phone: "", email: "", address: "", city: "", region_id: "", assigned_to: "", stage: "New", machine_catalog_id: "", potential_machines: [], notes: "" };

export default function Leads() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: me } = useAuth();
  const [view, setView] = useState("kanban");
  const [overdueOnly, setOverdueOnly] = useState(() => searchParams.get("overdue") === "1");
  const [leads, setLeads] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [convertLead, setConvertLead] = useState(null);
  const [reassignLead, setReassignLead] = useState(null);
  const [deleteLead, setDeleteLead] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [timelineLead, setTimelineLead] = useState(null);
  const [saleLead, setSaleLead] = useState(null); // auto-sale prompt when lead becomes Won
  const [dragId, setDragId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingLead, setEditingLead] = useState(null);  // null = create mode
  const [dupes, setDupes] = useState({ matching_leads: [], matching_clients: [] });
  const [regionFilter, setRegionFilter] = useState("");
  const [regionSuggestion, setRegionSuggestion] = useState(null);
  const [cascadePrompt, setCascadePrompt] = useState(null);  // { preview, payload, oldRegion, newRegion }

  const isAdmin = can(me, "leads", "admin");

  const loadAll = async () => {
    try {
      const [l, m, u, c] = await Promise.all([
        api.get("/leads"), api.get("/catalog"),
        api.get("/users?active_only=true"), api.get("/clients"),
      ]);
      setLeads(l.data);
      setCatalog(m.data);
      setUsers(u.data);
      setClients(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadAll(); }, []);

  // Deep link from dashboard: /leads?lead=<id> opens that lead's history.
  useEffect(() => {
    const id = searchParams.get("lead");
    if (!id || !leads.length) return;
    const found = leads.find((l) => l.id === id);
    if (found) setTimelineLead(found);
    const next = new URLSearchParams(searchParams);
    next.delete("lead");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line
  }, [leads.length, searchParams]);

  // Debounced duplicate check on email/phone change
  useEffect(() => {
    if (!open) { setDupes({ matching_leads: [], matching_clients: [] }); return; }
    const email = (form.email || "").trim();
    const phone = (form.phone || "").trim();
    if (!email && !phone) { setDupes({ matching_leads: [], matching_clients: [] }); return; }
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (email) params.append("email", email);
        if (phone) params.append("phone", phone);
        // When editing, don't flag the lead itself as its own duplicate.
        if (editingLead?.id) params.append("exclude_lead_id", editingLead.id);
        const res = await api.get(`/leads/duplicates?${params.toString()}`);
        setDupes(res.data);
      } catch (_e) { /* silent */ }
    }, 400);
    return () => clearTimeout(t);
  }, [form.email, form.phone, open, editingLead]);

  // Debounced region auto-suggest: if the user hasn't chosen a region yet, ask
  // the backend for a best guess based on city / address whenever those change.
  useEffect(() => {
    if (!open) { setRegionSuggestion(null); return; }
    if (form.region_id) { setRegionSuggestion(null); return; }
    const city = (form.city || "").trim();
    const address = (form.address || "").trim();
    if (!city && !address) { setRegionSuggestion(null); return; }
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (city) params.append("city", city);
        if (address) params.append("address", address);
        const res = await api.get(`/regions/suggest?${params.toString()}`);
        setRegionSuggestion(res.data?.region || null);
      } catch (_e) { setRegionSuggestion(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [form.city, form.address, form.region_id, open]);

  const openEdit = (lead) => {
    setEditingLead(lead);
    setForm({
      company_name: lead.company_name || "",
      contact_person: lead.contact_person || "",
      phone: lead.phone || "",
      email: lead.email || "",
      address: lead.address || "",
      city: lead.city || "",
      region_id: lead.region_id || "",
      assigned_to: lead.assigned_to || "",
      stage: lead.stage || "New",
      machine_catalog_id: lead.machine_catalog_id || "",
      potential_machines: (lead.potential_machines || []).map((p) => ({ catalog_id: p.catalog_id, quantity: p.quantity })),
      notes: "", // notes on edit form is only for adding *new* notes via timeline; leave blank
    });
    setOpen(true);
  };

  const resetForm = () => { setForm(EMPTY); setEditingLead(null); setRegionSuggestion(null); };

  const performUpdate = async (payload) => {
    try {
      await api.put(`/leads/${editingLead.id}`, payload);
      toast.success("Lead updated");
      setOpen(false);
      resetForm();
      loadAll();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name?.trim()) { toast.error("Company name is required"); return; }
    try {
      if (editingLead) {
        // Build update payload. `assigned_to` only if admin — otherwise keep existing.
        const payload = {
          company_name: form.company_name,
          contact_person: form.contact_person || "",
          phone: form.phone || "",
          email: form.email || "",
          address: form.address || "",
          city: form.city || "",
          region_id: form.region_id || "",
          assigned_to: isAdmin ? (form.assigned_to || editingLead.assigned_to || "") : (editingLead.assigned_to || ""),
          stage: form.stage,
          machine_catalog_id: form.machine_catalog_id || "",
          potential_machines: cleanPotentialMachines(form.potential_machines),
          notes: form.notes || "",
        };
        // If region changed, ask backend for cascade preview first.
        const regionChanged = (editingLead.region_id || "") !== (form.region_id || "");
        if (regionChanged) {
          try {
            const p = await api.get(`/leads/${editingLead.id}/region-cascade-preview?region_id=${encodeURIComponent(form.region_id || "")}`);
            if ((p.data.quotations_to_update || 0) + (p.data.bookings_to_update || 0) > 0) {
              setCascadePrompt({
                preview: p.data,
                payload,
                oldRegionName: editingLead.region_name || "Unassigned Region",
                newRegionName: p.data.new_region_name || (form.region_id ? "the selected region" : "Unassigned Region"),
              });
              return;
            }
          } catch (_e) { /* fall through */ }
        }
        await performUpdate(payload);
        return;
      }
      const payload = {
        ...form,
        machine_catalog_id: form.machine_catalog_id || "",
        potential_machines: cleanPotentialMachines(form.potential_machines),
        // Non-admin creators always own their own lead. Admins must pick a rep.
        assigned_to: isAdmin ? form.assigned_to : (me?.id || ""),
      };
      if (isAdmin && !payload.assigned_to) { toast.error("Pick a team member to assign this lead to"); return; }
      await api.post("/leads", payload);
      toast.success("Lead created");
      setOpen(false);
      resetForm();
      loadAll();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const onDragStart = (id) => {
    const lead = leads.find((l) => l.id === id);
    if (lead?.is_locked) { toast.error("This lead is locked — machine sold & dispatched."); return; }
    if (me?.role !== "admin" && lead?.assigned_to && lead.assigned_to !== me?.id) {
      toast.error("Only the assigned sales rep or an admin can move this lead.");
      return;
    }
    setDragId(id);
  };
  // Ask the backend for demoed inventory tied to this lead that isn't sold yet.
  const maybePromptSale = async (lead) => {
    if (!lead || lead.stage !== "PO Received" || lead.is_locked) return;
    try {
      const res = await api.get(`/leads/${lead.id}/sellable-units`);
      if (Array.isArray(res.data) && res.data.length > 0) {
        setSaleLead({ lead, units: res.data });
      }
    } catch (_e) { /* silent — prompt is optional */ }
  };

  const onDrop = async (stage) => {
    if (!dragId) return;
    const lead = leads.find((l) => l.id === dragId);
    if (!lead || lead.stage === stage) { setDragId(null); return; }
    setLeads((prev) => prev.map((l) => l.id === dragId ? { ...l, stage } : l));
    setDragId(null);
    try {
      const res = await api.patch(`/leads/${dragId}/stage`, { stage });
      setLeads((prev) => prev.map((l) => l.id === dragId ? { ...l, ...res.data, client: l.client, machine_catalog: l.machine_catalog, assigned_user: l.assigned_user } : l));
      if (stage === "PO Received") maybePromptSale({ ...lead, ...res.data });
    } catch (e) {
      toast.error(formatApiError(e));
      loadAll();
    }
  };

  const filteredLeads = useMemo(() => {
    let out = leads;
    if (regionFilter) {
      out = regionFilter === "__none__" ? out.filter((l) => !l.region_id) : out.filter((l) => l.region_id === regionFilter);
    }
    if (overdueOnly) out = out.filter((l) => l.is_overdue);
    return out;
  }, [leads, regionFilter, overdueOnly]);
  const grouped = STAGES.reduce((acc, s) => { acc[s] = filteredLeads.filter((l) => l.stage === s); return acc; }, {});
  const hasDupes = dupes.matching_leads.length + dupes.matching_clients.length > 0;

  return (
    <div data-testid="leads-page">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Pipeline</div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900">Leads</h1>
          <p className="text-slate-500 text-sm mt-1 hidden md:block">Self-contained leads — convert to a Client once they're Won.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-white border border-slate-200 rounded-md p-0.5">
            <button onClick={() => setView("kanban")} data-testid="view-kanban-btn" className={`px-3 py-1.5 text-xs font-medium rounded transition-colors duration-150 flex items-center gap-1.5 ${view === "kanban" ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:text-slate-900"}`}>
              <LayoutGrid className="w-3.5 h-3.5" /> Kanban
            </button>
            <button onClick={() => setView("list")} data-testid="view-list-btn" className={`px-3 py-1.5 text-xs font-medium rounded transition-colors duration-150 flex items-center gap-1.5 ${view === "list" ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:text-slate-900"}`}>
              <ListIcon className="w-3.5 h-3.5" /> List
            </button>
            <button onClick={() => setView("byperson")} data-testid="view-byperson-btn" className={`px-3 py-1.5 text-xs font-medium rounded transition-colors duration-150 flex items-center gap-1.5 ${view === "byperson" ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:text-slate-900"}`}>
              <Users2 className="w-3.5 h-3.5" /> By Sales Person
            </button>
            <button onClick={() => setView("byregion")} data-testid="view-byregion-btn" className={`px-3 py-1.5 text-xs font-medium rounded transition-colors duration-150 flex items-center gap-1.5 ${view === "byregion" ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:text-slate-900"}`}>
              <MapPin className="w-3.5 h-3.5" /> By Region
            </button>
          </div>
          {view !== "byregion" && (
            <RegionFilter value={regionFilter} onChange={setRegionFilter} testId="leads-region-filter" />
          )}
          <button
            onClick={() => setOverdueOnly(!overdueOnly)}
            data-testid="overdue-only-toggle"
            className={`px-3 py-1.5 text-xs font-medium rounded border transition-colors duration-150 flex items-center gap-1.5 ${
              overdueOnly
                ? "bg-rose-50 text-rose-700 border-rose-200"
                : "bg-white text-slate-600 border-slate-200 hover:text-slate-900"
            }`}
            title="Only show leads past their stage SLA"
          >
            <AlertTriangle className="w-3.5 h-3.5" /> Overdue only
            {overdueOnly && (
              <span className="ml-0.5 bg-rose-100 text-rose-700 rounded px-1 text-[10px] font-semibold">
                {filteredLeads.length}
              </span>
            )}
          </button>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
            <IfCan module="leads" level="edit"><DialogTrigger asChild>
              <Button onClick={() => { resetForm(); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-lead-btn"><Plus className="w-4 h-4 mr-1.5" /> New lead</Button>
            </DialogTrigger></IfCan>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editingLead ? `Edit lead — ${editingLead.company_name}` : "Create lead"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Company name *</Label>
                    <Input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="lead-company-input" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Contact person</Label>
                    <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="lead-contact-input" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Phone</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone-input" className="mt-1" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-email-input" className="mt-1" />
                  </div>
                  {hasDupes && (
                    <div className="sm:col-span-2 border border-amber-300 bg-amber-50 rounded-md p-3" data-testid="duplicate-warning">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-900 space-y-1">
                          <div className="font-semibold">Possible duplicate:</div>
                          {dupes.matching_leads.map((d) => (
                            <div key={"l" + d.id} data-testid={`dupe-lead-${d.id}`}>
                              Existing lead <b>{d.company_name}</b> ({d.stage}) — {d.email || d.phone}
                            </div>
                          ))}
                          {dupes.matching_clients.map((d) => (
                            <div key={"c" + d.id} data-testid={`dupe-client-${d.id}`}>
                              Existing client <b>{d.company_name}</b> — {d.email || d.phone}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Address</Label>
                    <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="lead-address-input" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">City</Label>
                    <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="lead-city-input" className="mt-1" placeholder="e.g. Bengaluru" />
                  </div>
                  <RegionSelect
                    value={form.region_id}
                    onChange={(v) => setForm({ ...form, region_id: v })}
                    suggestion={regionSuggestion}
                    testId="lead-region-select"
                  />
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Stage</Label>
                    <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                      <SelectTrigger className="mt-1" data-testid="lead-stage-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Assign to *</Label>
                    {isAdmin ? (
                      (() => {
                        const options = (users || []).filter((u) => u.is_active !== false);
                        return (
                          <Select value={form.assigned_to || ""} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                            <SelectTrigger className="mt-1" data-testid="lead-assigned-select">
                              <SelectValue placeholder={options.length ? "Pick a team member" : "Loading users…"} />
                            </SelectTrigger>
                            <SelectContent>
                              {options.length === 0 && (
                                <SelectItem value="__none__" disabled>No active users found</SelectItem>
                              )}
                              {options.map((u) => {
                                const subtitle = u.job_title || u.role_name || u.email;
                                return (
                                  <SelectItem key={u.id} value={u.id} data-testid={`lead-assigned-option-${u.id}`}>
                                    <span className="font-medium">{u.name}</span>
                                    {subtitle ? <span className="text-slate-500"> — {subtitle}</span> : null}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        );
                      })()
                    ) : (
                      <div className="mt-1 flex items-center gap-2 h-11 px-3 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-700" data-testid="lead-assigned-self">
                        <UserCog className="w-3.5 h-3.5 text-slate-400" />
                        {editingLead
                          ? (editingLead.assigned_user?.name || "This lead's current owner")
                          : (<><span>{me?.name}</span> <span className="text-xs text-slate-500">(you)</span></>)}
                        {editingLead && <span className="text-xs text-slate-500 ml-auto">(admin-only to change)</span>}
                      </div>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Model of interest</Label>
                    <Select value={form.machine_catalog_id || "none"} onValueChange={(v) => setForm({ ...form, machine_catalog_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="mt-1" data-testid="lead-model-select"><SelectValue placeholder="Select machine (optional)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {catalog.map((m) => <SelectItem key={m.id} value={m.id}>{m.model_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <PotentialMachinesEditor value={form.potential_machines || []} catalog={catalog}
                      onChange={(rows) => setForm((f) => ({ ...f, potential_machines: rows }))} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      {editingLead ? "Add a note (optional — appended to timeline)" : "Notes"}
                    </Label>
                    <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="lead-notes-input" className="mt-1" />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => { setOpen(false); resetForm(); }}>Cancel</Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="lead-save-btn">
                    {editingLead ? "Save changes" : "Create lead"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {view === "kanban" && (
        <div className="flex gap-4 overflow-x-auto kanban-scroll pb-2" data-testid="kanban-board">
          {STAGES.map((stage) => (
            <div key={stage} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(stage)}
              className={`w-80 flex-shrink-0 bg-white border border-slate-200 rounded-lg border-t-4 ${STAGE_COLORS[stage]}`}
              data-testid={`kanban-col-${stage.replace(/\s+/g, "-")}`}>
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <div className="font-heading font-semibold text-sm text-slate-900">{stage}</div>
                <span className="text-xs text-slate-500 bg-slate-100 rounded px-2 py-0.5">{grouped[stage].length}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[200px]">
                {grouped[stage].map((l) => {
                  const canEdit = !l.is_locked && (me?.role === "admin" || !l.assigned_to || l.assigned_to === me?.id);
                  return (
                  <div key={l.id} draggable={canEdit} onDragStart={() => onDragStart(l.id)}
                    className={`bg-white border rounded-md p-3 transition-colors duration-150 ${
                      l.is_overdue
                        ? "border-rose-300 bg-rose-50/40 border-l-4 border-l-rose-500"
                        : l.is_locked
                        ? "border-emerald-300 bg-emerald-50/30"
                        : "border-slate-200 hover:border-indigo-300 hover:shadow-sm"
                    }`}
                    data-testid={`lead-card-${l.id}`}>
                    <div className={`flex items-start justify-between gap-2 ${canEdit ? "cursor-grab active:cursor-grabbing" : "cursor-default"}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-1">
                          <div className="font-semibold text-sm text-slate-900 truncate flex-1">{l.company_name || "—"}</div>
                          <div className="flex items-center gap-0.5 shrink-0">
                            {!l.is_locked && (
                              <button
                                onClick={(e) => { e.stopPropagation(); openEdit(l); }}
                                className="text-slate-300 hover:text-indigo-600 p-1 -m-1"
                                title="Edit lead"
                                data-testid={`edit-btn-k-${l.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {isAdmin && !l.is_locked && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setDeleteLead(l); }}
                                className="text-slate-300 hover:text-rose-600 p-1 -m-1"
                                title="Delete lead"
                                data-testid={`delete-btn-k-${l.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">{l.contact_person}</div>
                        {l.machine_catalog && (
                          <div className="mt-2 text-xs inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-700 rounded">
                            {l.machine_catalog.model_name}
                          </div>
                        )}
                        {l.potential_machines?.length > 0 && (
                          <div className="mt-2"><PotentialMachinesSummary machines={l.potential_machines} testId={`pm-summary-k-${l.id}`} /></div>
                        )}
                        {l.assigned_user && <div className="text-xs text-slate-500 mt-2">Rep: {l.assigned_user.name}</div>}
                        <StageAgingChip lead={l} testId={`aging-chip-k-${l.id}`} />
                        {l.latest_quotation && (
                          <button type="button" onClick={(e) => { e.stopPropagation(); nav(`/quotations/${l.latest_quotation.id}`); }}
                            className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-600 hover:text-indigo-700" data-testid={`lead-latest-quote-${l.id}`}>
                            <FileText className="w-3 h-3" />
                            <span className="font-mono">{l.latest_quotation.quotation_number}</span>
                            <RevBadge revision={l.latest_quotation.revision} testId={`lead-quote-rev-${l.id}`} />
                            <span className="text-slate-400">· {l.latest_quotation.status}</span>
                          </button>
                        )}
                        {l.is_locked && (
                          <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5" data-testid={`lock-badge-${l.id}`}>
                            <Lock className="w-2.5 h-2.5" /> Locked · Deal closed
                          </div>
                        )}
                        {l.converted_client_id && (
                          <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                            Converted
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={() => setTimelineLead(l)}
                        data-testid={`history-btn-${l.id}`}
                        className="flex-1 flex items-center justify-center gap-1 text-xs text-slate-600 hover:text-indigo-600 border border-slate-200 hover:border-indigo-200 rounded py-1.5 transition-colors duration-150"
                      >
                        <History className="w-3 h-3" /> History ({(l.timeline || []).length})
                      </button>
                      {!l.is_locked && (
                        <IfCan module="quotations" level="edit">
                        <button
                          onClick={() => nav(`/quotations?lead=${l.id}`)}
                          data-testid={`quote-btn-${l.id}`}
                          className="flex-1 flex items-center justify-center gap-1 text-xs text-slate-600 hover:text-indigo-600 border border-slate-200 hover:border-indigo-200 rounded py-1.5 transition-colors duration-150"
                          title="Create a quotation for this lead"
                        >
                          <FileText className="w-3 h-3" /> Quote
                        </button>
                        </IfCan>
                      )}
                      {stage === "PO Received" && !l.converted_client_id && (
                        <Button variant="outline" size="sm" onClick={() => setConvertLead(l)}
                          className="flex-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50 h-auto py-1.5 text-xs"
                          data-testid={`convert-btn-${l.id}`}>
                          <ArrowRightCircle className="w-3 h-3 mr-1" /> Convert
                        </Button>
                      )}
                    </div>
                  </div>
                  );
                })}
                {grouped[stage].length === 0 && (
                  <div className="text-xs text-slate-400 text-center py-6">Drop leads here</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {view === "list" && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {/* Desktop table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Company</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Contact</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Stage</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Region</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Machine</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Assigned to</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10" data-testid="leads-empty">
                    <Target className="w-8 h-8 mx-auto mb-2 text-slate-300" /> No leads yet.
                  </TableCell></TableRow>
                ) : (
                  filteredLeads.map((l) => (
                    <TableRow key={l.id} className={`hover:bg-slate-50 ${l.is_overdue ? "bg-rose-50/40 border-l-4 border-l-rose-500" : l.is_locked ? "bg-emerald-50/30" : ""}`} data-testid={`lead-row-${l.id}`}>
                      <TableCell className="font-medium text-slate-900 text-sm">
                        <div className="flex items-center gap-2">
                          {l.company_name || "—"}
                          {l.is_locked && <Lock className="w-3.5 h-3.5 text-emerald-600" data-testid={`row-lock-${l.id}`} />}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">
                        <div>{l.contact_person}</div>
                        <div className="text-xs text-slate-500">{l.phone || l.email}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1.5">
                          <Badge variant="secondary">{l.stage}</Badge>
                          <StageAgingChip lead={l} compact testId={`aging-chip-row-${l.id}`} />
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">
                        {l.region_name ? (
                          <span className="inline-flex items-center gap-1 text-xs text-sky-700 bg-sky-50 border border-sky-100 rounded px-1.5 py-0.5" data-testid={`row-region-${l.id}`}>
                            <MapPin className="w-3 h-3" /> {l.region_name}
                          </span>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">
                        <div>{l.machine_catalog?.model_name || (l.potential_machines?.length ? "" : "—")}</div>
                        <PotentialMachinesSummary machines={l.potential_machines} testId={`pm-summary-row-${l.id}`} />
                      </TableCell>
                      <TableCell className="text-slate-600 text-sm">{l.assigned_user?.name || "—"}</TableCell>
                      <TableCell>
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" variant="outline" onClick={() => setTimelineLead(l)} data-testid={`history-btn-${l.id}`}>
                            <History className="w-3.5 h-3.5 mr-1" /> History
                          </Button>
                          {!l.is_locked && (
                            <Button size="sm" variant="outline" onClick={() => openEdit(l)} data-testid={`edit-btn-${l.id}`}>
                              <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                            </Button>
                          )}
                          {isAdmin && !l.is_locked && (
                            <Button size="sm" variant="outline" onClick={() => setReassignLead(l)} data-testid={`reassign-btn-${l.id}`}>
                              <UserCog className="w-3.5 h-3.5 mr-1" /> Reassign
                            </Button>
                          )}
                          {isAdmin && !l.is_locked && (
                            <Button size="sm" variant="outline" onClick={() => setDeleteLead(l)} className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700" data-testid={`delete-btn-${l.id}`}>
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                            </Button>
                          )}
                          {l.stage === "PO Received" && !l.converted_client_id && (
                            <Button size="sm" variant="outline" onClick={() => setConvertLead(l)} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid={`convert-btn-${l.id}`}>
                              <ArrowRightCircle className="w-3.5 h-3.5 mr-1" /> Convert
                            </Button>
                          )}
                          {l.converted_client_id && <span className="text-xs text-emerald-700 font-semibold self-center">Converted</span>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {/* Mobile card list */}
          <ul className="md:hidden divide-y divide-slate-100">
            {filteredLeads.length === 0 ? (
              <li className="text-center text-slate-400 py-10" data-testid="leads-empty-m">
                <Target className="w-8 h-8 mx-auto mb-2 text-slate-300" /> No leads yet.
              </li>
            ) : filteredLeads.map((l) => (
              <li key={l.id} className={`p-4 ${l.is_overdue ? "bg-rose-50/40 border-l-4 border-l-rose-500" : l.is_locked ? "bg-emerald-50/30" : "active:bg-slate-50"}`} data-testid={`lead-card-${l.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 text-[15px] truncate">{l.company_name || "—"}</span>
                      {l.is_locked && <Lock className="w-3.5 h-3.5 text-emerald-600 shrink-0" />}
                    </div>
                    <div className="text-sm text-slate-600 truncate">{l.contact_person}</div>
                    <div className="text-xs text-slate-500 truncate">{l.phone || l.email}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Badge variant="secondary">{l.stage}</Badge>
                    <StageAgingChip lead={l} compact testId={`aging-chip-m-${l.id}`} />
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                  {l.machine_catalog?.model_name && <span><Package className="w-3 h-3 inline mr-1" />{l.machine_catalog.model_name}</span>}
                  <PotentialMachinesSummary machines={l.potential_machines} testId={`pm-summary-m-${l.id}`} />
                  {l.region_name && <span className="inline-flex items-center gap-1 text-sky-700"><MapPin className="w-3 h-3" />{l.region_name}</span>}
                  {l.assigned_user?.name && <span>· {l.assigned_user.name}</span>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => setTimelineLead(l)} data-testid={`history-btn-m-${l.id}`}>
                    <History className="w-3.5 h-3.5 mr-1" /> History
                  </Button>
                  {!l.is_locked && (
                    <Button size="sm" variant="outline" onClick={() => openEdit(l)} data-testid={`edit-btn-m-${l.id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                  )}
                  {isAdmin && !l.is_locked && (
                    <Button size="sm" variant="outline" onClick={() => setReassignLead(l)} data-testid={`reassign-btn-m-${l.id}`}>
                      <UserCog className="w-3.5 h-3.5 mr-1" /> Reassign
                    </Button>
                  )}
                  {isAdmin && !l.is_locked && (
                    <Button size="sm" variant="outline" onClick={() => setDeleteLead(l)} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`delete-btn-m-${l.id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                    </Button>
                  )}
                  {l.stage === "PO Received" && !l.converted_client_id && (
                    <Button size="sm" variant="outline" onClick={() => setConvertLead(l)} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid={`convert-btn-m-${l.id}`}>
                      <ArrowRightCircle className="w-3.5 h-3.5 mr-1" /> Convert
                    </Button>
                  )}
                  {l.converted_client_id && <span className="text-xs text-emerald-700 font-semibold self-center">Converted</span>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {view === "byperson" && (
        <ByPersonView leads={filteredLeads} users={users} />
      )}

      {view === "byregion" && (
        <ByRegionView leads={leads} />
      )}

      <ConvertLeadDialog lead={convertLead} onClose={() => setConvertLead(null)} onConverted={() => { setConvertLead(null); loadAll(); }} />
      <ReassignLeadDialog lead={reassignLead} users={users} onClose={() => setReassignLead(null)} onReassigned={() => { setReassignLead(null); loadAll(); }} />
      <RegionCascadeConfirm
        data={cascadePrompt}
        onCancel={() => setCascadePrompt(null)}
        onConfirm={async () => {
          if (!cascadePrompt) return;
          const payload = cascadePrompt.payload;
          setCascadePrompt(null);
          await performUpdate(payload);
        }}
      />
      <ConfirmDelete
        open={!!deleteLead}
        onOpenChange={(v) => { if (!v) setDeleteLead(null); }}
        title="Delete this lead?"
        recordLabel={deleteLead?.company_name || ""}
        softDelete
        loading={deleting}
        confirmLabel="Archive lead"
        testIdPrefix="lead-confirm-delete"
        onConfirm={async () => {
          if (!deleteLead) return;
          setDeleting(true);
          try {
            await api.delete(`/leads/${deleteLead.id}`);
            toast.success(`Lead archived — restore anytime from the Audit Log`);
            setDeleteLead(null);
            loadAll();
          } catch (e) { toast.error(formatApiError(e)); }
          finally { setDeleting(false); }
        }}
      />
      <LeadTimelineDialog lead={timelineLead} onClose={() => setTimelineLead(null)} onNoteAdded={(updated) => { setTimelineLead(updated); loadAll(); }} />
      <RecordSaleDialog data={saleLead} clients={clients} onClose={() => setSaleLead(null)} onRecorded={() => { setSaleLead(null); loadAll(); }} />
    </div>
  );
}

// ---------- Convert Lead → Client dialog ----------
function ConvertLeadDialog({ lead, onClose, onConverted }) {
  if (!lead) return null;
  const initial = {
    company_name: lead.company_name || "",
    contact_person: lead.contact_person || "",
    phone: lead.phone || "",
    email: lead.email || "",
    billing_address: lead.address || "",
    gst_number: "",
    lead_source: "",
    shipping_addresses: [],
  };
  const submitFn = (payload) => api.post(`/leads/${lead.id}/convert`, payload).then((res) => ({ data: res.data.client }));
  return (
    <Dialog open={!!lead} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="convert-lead-dialog">
        <DialogHeader><DialogTitle>Convert lead to client</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 mb-2">Details are pre-filled from the lead. Review, add GST, and confirm to create the permanent Client record.</p>
        <ClientForm initial={initial} submitFn={submitFn} submitLabel="Create client" onCancel={onClose}
          onSuccess={() => { toast.success("Lead converted — Client created"); onConverted?.(); }} />
      </DialogContent>
    </Dialog>
  );
}

// ---------- Lead Timeline dialog ----------
function LeadTimelineDialog({ lead, onClose, onNoteAdded }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(lead);
  useEffect(() => { setCurrent(lead); setNote(""); }, [lead]);

  const events = useMemo(() => {
    const arr = [...((current && current.timeline) || [])];
    return arr.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  }, [current]);

  if (!current) return null;

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    try {
      const res = await api.post(`/leads/${current.id}/notes`, { note });
      setCurrent(res.data);
      setNote("");
      toast.success("Note added");
      onNoteAdded?.(res.data);
    } catch (e2) { toast.error(formatApiError(e2)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="lead-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>History — {current.company_name}</span>
            <Badge variant="secondary">{current.stage}</Badge>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={addNote} className="border border-slate-200 rounded-md p-3 bg-slate-50">
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Add a note</Label>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Called them — will get back next week."
            className="mt-1" data-testid="timeline-note-input" />
          <div className="flex justify-end mt-2">
            <Button type="submit" size="sm" disabled={saving || !note.trim()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="timeline-add-note-btn">
              {saving ? "Saving..." : "Post note"}
            </Button>
          </div>
        </form>

        <div className="mt-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Activity</div>
          {events.length === 0 ? (
            <div className="text-center text-slate-400 text-sm py-6">No history yet.</div>
          ) : (
            <ol className="relative border-l-2 border-slate-100 ml-3 space-y-4" data-testid="timeline-list">
              {events.map((e, i) => (
                <li key={i} className="ml-4" data-testid={`timeline-event-${e.type}`}>
                  <span className={`absolute -left-[9px] w-4 h-4 rounded-full flex items-center justify-center border-2 border-white ${
                    e.type === "created" ? "bg-slate-400" :
                    e.type === "stage_change" ? "bg-indigo-500" :
                    e.type === "note" ? "bg-amber-500" :
                    e.type === "converted" ? "bg-emerald-500" :
                    e.type === "locked" ? "bg-emerald-700" : "bg-slate-400"
                  }`}>
                    {e.type === "created" && <Sparkles className="w-2 h-2 text-white" />}
                    {e.type === "stage_change" && <ArrowRight className="w-2 h-2 text-white" />}
                    {e.type === "note" && <StickyNote className="w-2 h-2 text-white" />}
                    {e.type === "converted" && <UserPlus className="w-2 h-2 text-white" />}
                    {e.type === "locked" && <Lock className="w-2 h-2 text-white" />}
                  </span>
                  <div className="text-sm text-slate-900">
                    {e.type === "created" && <span>Lead created in stage <b>{e.stage}</b></span>}
                    {e.type === "stage_change" && <span>Moved from <b>{e.from}</b> to <b>{e.to}</b></span>}
                    {e.type === "note" && <span className="whitespace-pre-line">{e.note}</span>}
                    {e.type === "converted" && <span>Converted to Client <b>{e.company_name}</b></span>}
                    {e.type === "locked" && <span>Lead locked — machine <b>{e.serial_number || ""}</b> sold to <b>{e.client_name || "client"}</b></span>}
                    {e.type === "demo" && <span data-testid="timeline-demo-summary">{e.summary}</span>}
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                    <Clock className="w-3 h-3" />
                    {formatDMYTime(e.at)}
                    {e.actor && <span>· {e.actor}</span>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Auto-Sale Prompt (when a lead becomes Won) ----------
function RecordSaleDialog({ data, clients, onClose, onRecorded }) {
  const [unitId, setUnitId] = useState("");
  const [clientId, setClientId] = useState("");
  const [addressId, setAddressId] = useState("__billing__");
  const [soldDate, setSoldDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!data) { setUnitId(""); setClientId(""); setAddressId("__billing__"); return; }
    setUnitId(data.units?.[0]?.id || "");
    // Try to auto-select converted client if the lead was already converted, else leave blank
    setClientId(data.lead?.converted_client_id || "");
    setAddressId("__billing__");
    setSoldDate(new Date().toISOString().slice(0, 10));
  }, [data]);

  if (!data) return null;
  const client = clients.find((c) => c.id === clientId);

  const submit = async (e) => {
    e.preventDefault();
    if (!unitId) { toast.error("Choose a machine unit"); return; }
    if (!clientId) { toast.error("Choose the buyer (client)"); return; }
    setSaving(true);
    try {
      await api.patch(`/inventory/${unitId}/sell`, {
        client_id: clientId,
        address_id: addressId,
        sold_date: soldDate,
      });
      toast.success("Sale recorded — lead is now locked as Won.");
      onRecorded?.();
    } catch (e2) { toast.error(formatApiError(e2)); } finally { setSaving(false); }
  };

  const skip = () => {
    toast.info("You can record the sale later from the Inventory page.");
    onClose?.();
  };

  return (
    <Dialog open={!!data} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="record-sale-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-600" /> Deal closed — record the sale?
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600 -mt-2">
          <b>{data.lead?.company_name || "This lead"}</b> is now Won. Record the machine sale so the ledger and lock stay in lock-step.
        </p>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Machine unit *</Label>
            <Select value={unitId} onValueChange={setUnitId}>
              <SelectTrigger className="mt-1" data-testid="sale-unit-select"><SelectValue placeholder="Pick a demoed unit" /></SelectTrigger>
              <SelectContent>
                {data.units.map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`sale-unit-${u.id}`}>
                    {u.model_name} — {u.serial_number} · {u.status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Buyer (client) *</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="mt-1" data-testid="sale-client-select">
                <SelectValue placeholder={clients.length ? "Choose an existing client" : "No clients yet — convert the lead first"} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`sale-client-${c.id}`}>
                    {c.company_name} — {c.contact_person}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!clientId && !clients.length && (
              <p className="text-xs text-amber-700 mt-1">
                Convert this lead to a Client first (use the Convert button on the Won card), then record the sale.
              </p>
            )}
          </div>

          {client && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Ship to address *</Label>
              <Select value={addressId} onValueChange={setAddressId}>
                <SelectTrigger className="mt-1" data-testid="sale-address-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {client.billing_address && <SelectItem value="__billing__">Billing — {client.billing_address.slice(0, 40)}</SelectItem>}
                  {(client.shipping_addresses || []).map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.label} — {(a.line || "").slice(0, 40)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sale date</Label>
            <Input type="date" value={soldDate} onChange={(e) => setSoldDate(e.target.value)} className="mt-1" data-testid="sale-date-input" />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={skip} data-testid="sale-skip-btn">Skip for now</Button>
            <Button type="submit" disabled={saving || !unitId || !clientId} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sale-record-btn">
              {saving ? "Recording..." : "Record sale"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Reassign lead dialog (Admin-only) ----------
function ReassignLeadDialog({ lead, users, onClose, onReassigned }) {
  const [newAssignee, setNewAssignee] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNewAssignee(""); }, [lead?.id]);
  if (!lead) return null;
  // Any active user can own a lead — Admins, Managers or Sales Reps.
  const reps = (users || []).filter((u) => u.is_active !== false);
  const currentAssignee = users.find((u) => u.id === lead.assigned_to);
  const submit = async (e) => {
    e.preventDefault();
    if (!newAssignee) { toast.error("Pick a team member"); return; }
    if (newAssignee === lead.assigned_to) { toast.error("Already assigned to that person"); return; }
    setSaving(true);
    try {
      await api.post(`/leads/${lead.id}/reassign`, { assigned_to: newAssignee });
      toast.success("Lead reassigned — quotations and client ownership transferred");
      onReassigned?.();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!lead} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="reassign-lead-dialog">
        <DialogHeader><DialogTitle>Reassign lead</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm">
            <div className="text-slate-600">Lead</div>
            <div className="font-semibold text-slate-900">{lead.company_name}</div>
            <div className="text-xs text-slate-500 mt-1">Currently owned by <b>{currentAssignee?.name || "— (unassigned)"}</b></div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reassign to *</Label>
            <Select value={newAssignee} onValueChange={setNewAssignee}>
              <SelectTrigger className="mt-1" data-testid="reassign-user-select"><SelectValue placeholder="Pick a team member" /></SelectTrigger>
              <SelectContent>
                {reps.length === 0 && <SelectItem value="none" disabled>No active users</SelectItem>}
                {reps.map((u) => {
                  const subtitle = u.job_title || u.role_name || u.email;
                  return (
                    <SelectItem key={u.id} value={u.id} disabled={u.id === lead.assigned_to}>
                      <span className="font-medium">{u.name}</span>
                      {subtitle ? <span className="text-slate-500"> — {subtitle}</span> : null}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
            Every open quotation on this lead — and the linked client, if converted — transfers to the new rep. The previous owner will lose access immediately.
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving || !newAssignee} className="bg-indigo-600 hover:bg-indigo-700" data-testid="reassign-submit-btn">
              {saving ? "Reassigning..." : "Reassign lead"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- By-Sales-Person grouped view ----------
function ByPersonView({ leads, users }) {
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState({});
  const toggle = (id) => setCollapsed((p) => ({ ...p, [id]: !p[id] }));

  // Build a name/title map from `users` (loaded on the parent page) so we can
  // show a friendly header even if `assigned_user` on the lead is stale.
  const userMap = Object.fromEntries((users || []).map((u) => [u.id, u]));

  // Group by assigned_to
  const groups = {};
  (leads || []).forEach((l) => {
    const key = l.assigned_to || "__unassigned__";
    if (!groups[key]) groups[key] = { leads: [], stageCounts: {} };
    groups[key].leads.push(l);
    groups[key].stageCounts[l.stage] = (groups[key].stageCounts[l.stage] || 0) + 1;
  });

  const groupList = Object.entries(groups)
    .map(([id, g]) => {
      const u = id === "__unassigned__" ? null : (userMap[id] || g.leads[0]?.assigned_user || null);
      return {
        id,
        name: u?.name || (id === "__unassigned__" ? "Unassigned" : "Unknown user"),
        subtitle: u?.job_title || u?.role_name || u?.email || "",
        leads: g.leads,
        stageCounts: g.stageCounts,
      };
    })
    .sort((a, b) => b.leads.length - a.leads.length);

  if (groupList.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-400" data-testid="byperson-empty">
        <Users2 className="w-8 h-8 mx-auto mb-2 text-slate-300" />
        No leads to group yet.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="byperson-view">
      {groupList.map((g) => {
        const open = !collapsed[g.id];
        return (
          <section key={g.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`byperson-group-${g.id}`}>
            <button
              type="button"
              onClick={() => toggle(g.id)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
              data-testid={`byperson-toggle-${g.id}`}
            >
              {open ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
              <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold flex items-center justify-center shrink-0">
                {(g.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm truncate">
                  {g.name}
                  {g.subtitle ? <span className="text-slate-500 font-normal"> — {g.subtitle}</span> : null}
                  <span className="text-slate-500 font-normal"> ({g.leads.length} lead{g.leads.length === 1 ? "" : "s"})</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                  {STAGES.map((s) => (g.stageCounts[s] || 0) > 0 ? `${g.stageCounts[s]} ${s}` : null)
                    .filter(Boolean).join("  ·  ")}
                </div>
              </div>
            </button>
            {open && (
              <>
                {/* Desktop table */}
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Company</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Stage</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Model of interest</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Created</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold w-32"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {g.leads.map((l) => (
                        <TableRow key={l.id} className="hover:bg-slate-50" data-testid={`byperson-row-${l.id}`}>
                          <TableCell>
                            <div className="font-medium text-slate-900">{l.company_name || "—"}</div>
                            <div className="text-xs text-slate-500">{l.contact_person}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-[10px] ${STAGE_COLORS[l.stage] || ""} bg-transparent`}>{l.stage}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {l.machine_catalog?.model_name || "—"}
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">
                            {l.created_at ? new Date(l.created_at).toLocaleDateString("en-GB") : "—"}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              <IfCan module="quotations" level="edit"><Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => nav(`/quotations?lead=${l.id}`)} data-testid={`byperson-quote-${l.id}`}>
                                Quote
                              </Button></IfCan>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile stacked cards */}
                <ul className="md:hidden divide-y divide-slate-100">
                  {g.leads.map((l) => (
                    <li key={l.id} className="p-4" data-testid={`byperson-card-m-${l.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate">{l.company_name || "—"}</div>
                          <div className="text-xs text-slate-500 truncate">{l.contact_person}</div>
                        </div>
                        <Badge variant="outline" className={`text-[10px] ${STAGE_COLORS[l.stage] || ""} bg-transparent shrink-0`}>{l.stage}</Badge>
                      </div>
                      <div className="mt-2 text-xs text-slate-500 flex items-center justify-between">
                        <span className="truncate">{l.machine_catalog?.model_name || "—"}</span>
                        <span>{l.created_at ? new Date(l.created_at).toLocaleDateString("en-GB") : ""}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        );
      })}
    </div>
  );
}

// ---------- By-Region grouped view ----------
function ByRegionView({ leads }) {
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState({});
  const toggle = (id) => setCollapsed((p) => ({ ...p, [id]: !p[id] }));

  // Group by region_id — unassigned goes last.
  const groups = {};
  (leads || []).forEach((l) => {
    const key = l.region_id || "__unassigned__";
    if (!groups[key]) groups[key] = { name: l.region_name || (key === "__unassigned__" ? "Unassigned Region" : "Unknown region"), leads: [], stageCounts: {} };
    groups[key].leads.push(l);
    groups[key].stageCounts[l.stage] = (groups[key].stageCounts[l.stage] || 0) + 1;
  });

  const groupList = Object.entries(groups)
    .map(([id, g]) => ({ id, ...g }))
    .sort((a, b) => {
      if (a.id === "__unassigned__") return 1;
      if (b.id === "__unassigned__") return -1;
      return b.leads.length - a.leads.length;
    });

  if (groupList.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-400" data-testid="byregion-empty">
        <MapPin className="w-8 h-8 mx-auto mb-2 text-slate-300" />
        No leads to group yet. Assign a region to your leads to see the rollup here.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="byregion-view">
      {groupList.map((g) => {
        const open = !collapsed[g.id];
        return (
          <section key={g.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`byregion-group-${g.id}`}>
            <button
              type="button"
              onClick={() => toggle(g.id)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
              data-testid={`byregion-toggle-${g.id}`}
            >
              {open ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
              <div className="w-8 h-8 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm truncate" data-testid={`byregion-name-${g.id}`}>
                  {g.name}
                  <span className="text-slate-500 font-normal"> — {g.leads.length} lead{g.leads.length === 1 ? "" : "s"}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 truncate" data-testid={`byregion-breakdown-${g.id}`}>
                  {STAGES.map((s) => (g.stageCounts[s] || 0) > 0 ? `${g.stageCounts[s]} ${s}` : null)
                    .filter(Boolean).join("  ·  ") || "No open leads"}
                </div>
              </div>
            </button>
            {open && (
              <>
                {/* Desktop */}
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Company</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Stage</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Model</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Owner</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold w-24"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {g.leads.map((l) => (
                        <TableRow key={l.id} className="hover:bg-slate-50" data-testid={`byregion-row-${l.id}`}>
                          <TableCell>
                            <div className="font-medium text-slate-900">{l.company_name || "—"}</div>
                            <div className="text-xs text-slate-500">{l.contact_person}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-[10px] ${STAGE_COLORS[l.stage] || ""} bg-transparent`}>{l.stage}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">{l.machine_catalog?.model_name || "—"}</TableCell>
                          <TableCell className="text-sm text-slate-600">{l.assigned_user?.name || "—"}</TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              <IfCan module="quotations" level="edit"><Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => nav(`/quotations?lead=${l.id}`)} data-testid={`byregion-quote-${l.id}`}>
                                Quote
                              </Button></IfCan>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile */}
                <ul className="md:hidden divide-y divide-slate-100">
                  {g.leads.map((l) => (
                    <li key={l.id} className="p-4" data-testid={`byregion-card-m-${l.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate">{l.company_name || "—"}</div>
                          <div className="text-xs text-slate-500 truncate">{l.contact_person}</div>
                        </div>
                        <Badge variant="outline" className={`text-[10px] ${STAGE_COLORS[l.stage] || ""} bg-transparent shrink-0`}>{l.stage}</Badge>
                      </div>
                      <div className="mt-2 text-xs text-slate-500 flex items-center justify-between">
                        <span className="truncate">{l.machine_catalog?.model_name || "—"}</span>
                        <span>{l.assigned_user?.name || ""}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        );
      })}
    </div>
  );
}


// ---------- Region cascade confirmation dialog ----------
function RegionCascadeConfirm({ data, onCancel, onConfirm }) {
  if (!data) return null;
  const { preview, oldRegionName, newRegionName } = data;
  const q = preview.quotations_to_update || 0;
  const b = preview.bookings_to_update || 0;
  const qOver = preview.quotations_overridden || 0;
  const bOver = preview.bookings_overridden || 0;
  return (
    <Dialog open={!!data} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="region-cascade-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-sky-600" />
            Apply region change to linked records?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-slate-700">
            You're moving this lead from <b>{oldRegionName}</b> to <b>{newRegionName}</b>.
          </p>
          <ul className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-1 text-slate-700">
            {q > 0 && (
              <li data-testid="cascade-q-count">
                <b>{q}</b> quotation{q === 1 ? "" : "s"} will follow to <b>{newRegionName}</b>
              </li>
            )}
            {b > 0 && (
              <li data-testid="cascade-b-count">
                <b>{b}</b> demo booking{b === 1 ? "" : "s"} will follow to <b>{newRegionName}</b>
              </li>
            )}
            {(qOver + bOver) > 0 && (
              <li className="text-slate-500 text-xs mt-2 pt-2 border-t border-slate-200" data-testid="cascade-overridden-count">
                {qOver > 0 && `${qOver} quotation${qOver === 1 ? "" : "s"}`}
                {qOver > 0 && bOver > 0 && " and "}
                {bOver > 0 && `${bOver} demo${bOver === 1 ? "" : "s"}`}
                {" "}were manually overridden and will stay put.
              </li>
            )}
          </ul>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} data-testid="cascade-cancel-btn">Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={onConfirm} data-testid="cascade-confirm-btn">
            Apply &amp; save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Days-in-stage SLA chip ----------
// Compact badge that shows how many business days a lead has been in its
// current stage. Flips to a red "⚠ overdue" state once SLA is breached.
// Terminal stages (PO Received, Lost) get no chip.
function StageAgingChip({ lead, compact = false, testId }) {
  if (!lead || TERMINAL_STAGES.has(lead.stage) || lead.stage_sla == null) return null;
  const days = lead.days_in_stage ?? 0;
  const overdue = !!lead.is_overdue;
  const by = overdue ? ` · +${days - (lead.stage_sla || 0)}` : "";
  const label = `${days} business ${days === 1 ? "day" : "days"}${overdue ? ` ⚠${by}` : ""}`;
  return (
    <span
      data-testid={testId}
      data-overdue={overdue ? "true" : "false"}
      title={overdue
        ? `Overdue — SLA for ${lead.stage} is ${lead.stage_sla} business days.`
        : `SLA for ${lead.stage}: ${lead.stage_sla} business days.`}
      className={[
        "inline-flex items-center gap-1 rounded border font-medium",
        compact ? "text-[10px] px-1.5 py-0.5" : "text-[11px] px-2 py-0.5 mt-2",
        overdue
          ? "bg-rose-100 border-rose-300 text-rose-700"
          : days >= Math.max(1, (lead.stage_sla || 0) - 1)
          ? "bg-amber-50 border-amber-200 text-amber-700"
          : "bg-slate-50 border-slate-200 text-slate-600",
      ].join(" ")}
    >
      <Clock className="w-3 h-3" /> {label}
    </span>
  );
}

